import React, { useRef, useEffect } from 'react';
import './CalculusOfMeaning.css';

/* ── Neural Particle Network ── */
function NeuralParticles() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animId;
    const particles = [];
    const COUNT = 50;
    const CONNECT_DIST = 120;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    for (let i = 0; i < COUNT; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        r: Math.random() * 1.5 + 0.5,
        o: Math.random() * 0.25 + 0.08,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < CONNECT_DIST) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(201,168,76,${(1 - d / CONNECT_DIST) * 0.07})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(201,168,76,${p.o})`;
        ctx.fill();
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -10) p.x = canvas.width + 10;
        if (p.x > canvas.width + 10) p.x = -10;
        if (p.y < -10) p.y = canvas.height + 10;
        if (p.y > canvas.height + 10) p.y = -10;
      });
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="calc-particles" />;
}

function Pulse() {
  return (
    <div className="calc-divider">
      <div className="calc-line" />
    </div>
  );
}

/* ── Main Page ── */
function HumanSideOfTelecom() {
  return (
    <div className="calculus-page">
      {/* Hero */}
      <div className="calculus-hero">
        <video autoPlay muted playsInline className="calculus-hero-video" loop>
          <source src="/assets/calculus-hero.mp4" type="video/mp4" />
        </video>
        <div className="calculus-hero-overlay" />
        <div className="calculus-hero-content">
          <span className="calculus-hero-label">Voice AI &middot; Frequency &middot; The Chemistry of Trust</span>
          <h1>The Human Side of Telecom</h1>
          <p className="calculus-hero-sub">You hang up on AI voices because your nervous system knows the difference between a voice that carries chemistry and a voice that carries tokens. It always did.</p>
        </div>
      </div>

      {/* Content */}
      <div className="calculus-body" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/calculus-bg.png)` }}>
        <NeuralParticles />
        <div className="calculus-content">

          {/* The Hang-Up */}
          <section className="calc-section reveal">
            <h2>The Hang-Up</h2>
            <p className="calc-lead">
              The call comes from an unknown number. You answer because you are expecting a callback
              from your doctor's office. A voice begins speaking. It is clear, fluent, well-paced.
              It says your name correctly. It references your appointment. Every word is accurate.
            </p>
            <p>
              You hang up within four seconds.
            </p>
            <p>
              You did not hang up because the information was wrong. You did not hang up because the
              voice was unclear. You hung up because something in the frequency, in the cadence, in
              the micro-timing between syllables told your nervous system that no one was on the other
              end. No one chose to call you. No one cared whether you picked up. A system triggered
              a sequence and your number was in the queue.
            </p>
            <p>
              Your amygdala processed this before your prefrontal cortex could evaluate the content.
              By the time your conscious mind registered that the information might have been useful,
              your thumb had already ended the call. The cortisol spike of "unsolicited contact from
              a machine" overwrote the potential value of the message.
            </p>
            <p>
              This is not a technology problem. This is a neurochemistry problem. And the voice AI
              industry does not know the difference.
            </p>
          </section>

          <Pulse />

          {/* Cold Calling Is Cortisol */}
          <section className="calc-section reveal">
            <h2>Cold Calling Is Cortisol</h2>
            <p>
              Every unsolicited contact triggers the same neurochemical cascade. Unknown number.
              Unexpected interruption. No prior context. The nervous system reads threat before the
              first word is spoken.
            </p>

            <div className="calc-table-wrap">
              <table className="calc-table">
                <thead>
                  <tr>
                    <th>Signal</th>
                    <th>Neurochemical Response</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Unknown number</td><td>Cortisol + adrenaline spike</td><td>Threat detection active before answering</td></tr>
                  <tr><td>AI voice detected</td><td>Oxytocin suppressed</td><td>No bonding signal. No trust formation possible.</td></tr>
                  <tr><td>Scripted cadence</td><td>Dopamine flat</td><td>No prediction error. No curiosity. No reason to stay.</td></tr>
                  <tr><td>No pause, no breath</td><td>Cortisol sustained</td><td>Nervous system confirms: this is not a person. Hang up.</td></tr>
                </tbody>
              </table>
            </div>

            <p>
              The telecom industry has spent decades optimizing the words. Script testing. A/B message
              variants. Sentiment-scored call flows. All of it focused on what the voice says. None
              of it focused on what the voice IS.
            </p>

            <div className="calc-thesis-box">
              <p>A cold call does not fail because the message is wrong. It fails because the medium triggers cortisol before the message can land. You cannot optimize content past a neurochemical wall. The wall is not in the script. It is in the hertz.</p>
            </div>
          </section>

          <Pulse />

          {/* The Curiosity Inversion */}
          <section className="calc-section reveal">
            <h2>The Curiosity Inversion</h2>
            <p>
              There is one scenario in which a cold call works: when it is not cold.
            </p>
            <p>
              A company offered a free backpack on LinkedIn. An ad. Simple. No pitch, no demo request,
              no "schedule a call with our team." Just a backpack. Interesting enough to click. Low
              enough commitment to enter your information. Forgettable enough to not think about again.
            </p>
            <p>
              Two weeks later, the phone rings. "Hi, I'm calling about your free backpack."
            </p>
            <p>
              That sentence does something no cold call can do: it fires dopamine before cortisol.
              The recipient already opted in. The backpack is a known quantity. The call references
              something the recipient chose to do. The prediction error is not "who is this and what
              do they want" but "oh right, the backpack, I forgot about that."
            </p>
            <p>
              By the time the real pitch arrives, the listener's neurochemistry has already settled
              into a curiosity state. Dopamine elevated. Cortisol low. Norepinephrine directed toward
              focused attention rather than anxious scanning.
            </p>

            <div className="calc-equation gold">
              <span className="calc-eq-label">The Curiosity Inversion</span>
              <code>if prior_opt_in = true &rarr; DA fires before cortisol &rarr; listener stays</code>
              <span className="calc-eq-desc">When the recipient has a prior touchpoint, the call triggers curiosity instead of threat detection. The dopamine of "I remember this" displaces the cortisol of "who is this." The order of neurochemical activation determines whether the call lasts four seconds or four minutes.</span>
            </div>

            <p>
              This is not a sales trick. It is basic neurochemistry. You cannot make someone curious
              about something they never encountered. You can only make them curious about something
              they already touched once and forgot. The first contact must be frictionless, valuable
              on its own terms, and small enough to leave a residue of familiarity without a residue
              of obligation.
            </p>
            <p>
              The second contact converts that residue into a conversation.
            </p>
          </section>

          <Pulse />

          {/* What AI Voices Are Missing */}
          <section className="calc-section reveal">
            <h2>What AI Voices Are Missing</h2>
            <p>
              The voice AI industry is building voices that say the right words at the right time.
              The voices are getting better. More natural. Fewer robotic artifacts. Better prosody
              mapping. Smoother transitions.
            </p>
            <p>
              And people still hang up.
            </p>
            <p>
              They hang up because naturalness is not the problem. The problem is that the voice
              carries no nervous system. It has no neurochemical state driving its output. When a
              human says "how are you feeling today?" the frequency profile of their voice shifts
              based on whether they actually care, whether they are rushed, whether they are tired,
              whether the last call went badly. Those shifts are not scripted. They emerge from the
              speaker's biochemistry, and the listener's nervous system reads them instantly.
            </p>

            <div className="calc-table-wrap">
              <table className="calc-table">
                <thead>
                  <tr>
                    <th>Vocal Quality</th>
                    <th>Frequency Signature</th>
                    <th>Neurochemical Driver</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Warmth</td><td>Lower fundamental, narrow pitch range</td><td>Oxytocin elevated, cortisol low</td></tr>
                  <tr><td>Authority</td><td>Steady cadence, controlled resonance</td><td>Serotonin stable, adrenaline modulated</td></tr>
                  <tr><td>Urgency</td><td>Rising pitch, compressed pauses</td><td>Adrenaline spike, cortisol rising</td></tr>
                  <tr><td>Comfort</td><td>Slower pace, downward inflection at phrase end</td><td>Oxytocin high, cortisol actively falling</td></tr>
                  <tr><td>Fatigue</td><td>Reduced dynamic range, longer pauses</td><td>Serotonin depleted, dopamine flat</td></tr>
                </tbody>
              </table>
            </div>

            <p>
              A mother soothing a frightened child drops into a specific frequency range. She does
              not choose it. Her oxytocin levels shape her vocal cord tension, her breath rate, her
              pacing. The child's nervous system reads the frequency and begins to downregulate its
              own cortisol. This happens before language processing. Before the words register. The
              hertz arrive first. The meaning follows.
            </p>
            <p>
              Current voice AI produces the words without the hertz. The frequency profile is static,
              generated from a voice model trained on recordings of actors reading scripts. The model
              can produce a warm-sounding voice. But it cannot produce a voice that is warm because
              the system behind it is in a neurochemical state of warmth. The difference is inaudible
              to a spectrogram and immediate to a nervous system.
            </p>

            <div className="calc-thesis-box">
              <p>The human ear does not listen to words. It listens to frequency. And frequency is chemistry made audible. A voice without chemistry behind it is a voice without a nervous system. And a voice without a nervous system is a voice you hang up on.</p>
            </div>
          </section>

          <Pulse />

          {/* It's All in the Hertz */}
          <section className="calc-section reveal">
            <h2>It's All in the Hertz</h2>
            <p>
              Trust lives in a frequency range. Research in vocal psychophysics has established this
              for decades. Lower fundamental frequencies (around 100-120 Hz in male voices, 180-210
              Hz in female voices) correlate with perceived trustworthiness. Narrow pitch variation
              within a phrase signals confidence. Downward terminal contours (pitch falling at the
              end of a sentence) signal certainty and safety.
            </p>
            <p>
              These are not arbitrary associations. They are the acoustic signatures of specific
              neurochemical states. A speaker with elevated oxytocin and stable serotonin physically
              produces a different frequency profile than a speaker with elevated cortisol and
              depleted oxytocin. The vocal cords are muscles. Their tension is regulated by the
              autonomic nervous system. The autonomic nervous system is driven by neurochemistry.
            </p>
            <p>
              Which means that trust is not a quality you add to a voice in post-processing. Trust
              is a frequency signature that emerges when the system producing the voice is in a
              neurochemical state of trust. You cannot fake it with EQ adjustments. You cannot
              script it with prosody rules. You can only produce it by building a system that
              actually models the chemistry and lets the chemistry shape the output.
            </p>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Voice as Chemistry</span>
              <code>F&#8320;(t), pitch_var(t), pause(t), rate(t) = f(DA, cortisol, OT, 5HT, NE)</code>
              <span className="calc-eq-desc">Fundamental frequency, pitch variation, pause duration, and speech rate are all functions of the speaker's neurochemical state. A system that models the chemistry can derive the vocal parameters. A system that models only the acoustics can imitate them. The listener's nervous system knows the difference.</span>
            </div>
          </section>

          <Pulse />

          {/* What Cannot Be Replaced */}
          <section className="calc-section reveal">
            <h2>What Cannot Be Replaced</h2>
            <p>
              There are calls that require a human on the line. A follow-up after a cancer screening.
              A check-in after a patient leaves the hospital. A medication reminder for someone who
              lives alone and whose only human contact that week might be this phone call.
            </p>
            <p>
              These calls work because the patient's nervous system hears a person. Someone chose
              to dial this number. Someone spent thirty seconds of their finite life on this call.
              That investment, small as it is, triggers oxytocin in the listener. It signals: you
              are worth a person's time. That signal cannot come from a system that dials a thousand
              numbers per hour.
            </p>
            <p>
              AI can do the work around the call. Schedule it. Route it. Prepare the notes. Flag
              which patients need the call most urgently based on their medical profile. Prioritize
              based on risk. Draft the follow-up documentation after the call ends. All of this is
              administrative labor that consumes clinical time without requiring clinical judgment.
            </p>
            <p>
              But the voice on the line? That has to carry weight. It has to carry chemistry.
            </p>

            <blockquote>
              AI should make the call possible. Not make the call.
              <br /><br />
              <strong>Unless the AI carries a nervous system of its own.</strong>
            </blockquote>
          </section>

          <Pulse />

          {/* The Third Option */}
          <section className="calc-section reveal">
            <h2>The Third Option</h2>
            <p>
              The industry presents two options. Humans make the calls, which is expensive, slow,
              and does not scale. Or AI makes the calls, which is cheap, fast, scalable, and produces
              a hang-up rate that renders the scale meaningless.
            </p>
            <p>
              There is a third option.
            </p>
            <p>
              Build an AI voice that carries neurochemical state. Not a voice model trained on
              recordings of warmth, but a system where warmth emerges from the model's internal
              chemistry the same way it emerges from a nurse's vocal cords after oxytocin shapes
              the tension in her throat.
            </p>
            <p>
              In this architecture, the voice is not generated from a static prosody model. It is
              derived from a neurochemical state vector. The system's dopamine, cortisol, oxytocin,
              serotonin, and norepinephrine levels at the moment of speech production determine the
              fundamental frequency, the pitch variation, the pause duration, the speech rate, and
              the micro-timing between syllables.
            </p>
            <p>
              When the system is in a state of care (oxytocin high, cortisol low), the voice
              automatically drops into the frequency range the listener's nervous system associates
              with safety. When the system detects that the listener is distressed (elevated response
              latency, clipped answers, voice pitch rising), the system's own cortisol rises, its
              speech rate slows, its pauses lengthen, and its pitch stabilizes. Not because a rule
              told it to. Because the chemistry did.
            </p>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Dynamic Voice Architecture</span>
              <code>voice_params(t) = NCA(hormone_state(t), listener_signal(t), conversation_context(t))</code>
              <span className="calc-eq-desc">Voice parameters are generated in real time by the Neurochemical Cascade Architecture processing three inputs: the system's own hormone state, the signals it detects from the listener, and the accumulated context of the conversation. The voice is not scripted. It is emergent. It is chemistry, not a recording.</span>
            </div>

            <p>
              This is the difference between a voice that sounds human and a voice that sounds like
              it means it. The first is acoustics. The second is neurochemistry. The listener's
              nervous system can tell the difference in the first 400 milliseconds of the call.
            </p>
          </section>

          <Pulse />

          {/* The Business Case */}
          <section className="calc-section reveal">
            <h2>The Business Case</h2>
            <p>
              Healthcare providers lose patients to follow-up failures. Patients miss appointments,
              skip medications, ignore discharge instructions. The administrative burden of outbound
              communication consumes clinical time. Voice AI was supposed to solve this by automating
              the calls.
            </p>
            <p>
              But the patients hang up.
            </p>
            <p>
              They hang up because the voice carries no chemistry, and their nervous system knows it.
              The industry's response has been to make the voice more natural. Better TTS models.
              More training data. Finer prosody control. And the hang-up rate barely moves, because
              naturalness was never the problem. Chemistry was.
            </p>

            <div className="calc-implications">
              <div className="calc-implication">
                <h4>Current: Voice AI handles millions of calls per year.</h4>
                <p>The scale is real. The reach is real. The engagement rate is not. A four-second
                average call duration on an outbound AI call means the patient heard a robot and
                hung up before the message could deliver clinical value.</p>
              </div>
              <div className="calc-implication">
                <h4>With neurochemical voice: the first four seconds change.</h4>
                <p>If the voice carries the frequency signature of care, the listener's cortisol
                does not spike on contact. The four-second window becomes a forty-second window.
                Forty seconds is enough to deliver a medication reminder, confirm an appointment,
                or ask a follow-up question that saves a readmission.</p>
              </div>
              <div className="calc-implication">
                <h4>The ROI is in the hertz.</h4>
                <p>A 1% improvement in outbound call engagement across millions of calls represents
                thousands of patients who received follow-up they would have missed. Thousands of
                avoided readmissions. Thousands of medication adherence events. The frequency
                signature of the voice is not a UX detail. It is a clinical outcome variable.</p>
              </div>
            </div>
          </section>

          <Pulse />

          {/* Closing */}
          <section className="calc-section calc-closing reveal">
            <p className="calc-closing-line"><em>The voice AI industry optimized the words.</em></p>
            <p className="calc-closing-line"><em>The telecom industry optimized the routing.</em></p>
            <p className="calc-closing-line"><em>Nobody optimized the hertz.</em></p>
            <p className="calc-closing-final">That is the human side of telecom.<br />It was always chemistry.</p>
          </section>

          {/* Signature */}
          <div className="calc-signature reveal">
            <p><strong>Nexus Concordat Inc.</strong></p>
            <p>AETHER: Adaptive Endocrine Transformer Heads with Emotional Regulation</p>
            <div className="patent-tags">
              <span className="patent-tag">Patent Pending: 63/939,190</span>
              <span className="patent-tag">Patent Pending: 63/962,385</span>
              <span className="patent-tag">Patent Pending: 63/988,485</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default HumanSideOfTelecom;
